import { FormEvent, MouseEvent, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { UseOpenCloseReturn } from '~/core/hooks/useOpenClose';
import { useRunBankStatementsBrokerRequest } from '~/core/shared/api/plugin';
import {
  BROKER_DOCUMENT_ID_ERROR,
  BROKER_DOCUMENT_ID_PATTERN,
} from '~/core/shared/constants';
import { useFormResolver } from '~/core/shared/hooks';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { BankStatementsBrokerFormValues } from '../../types';

type UseBankStatementsBrokerFormOptions = {
  uuid: string;
  definition: string;
  onSuccess?: () => void;
};

const DEFAULT_VALUES: BankStatementsBrokerFormValues = {
  brokerDocumentId: '',
  sync: false,
};

// TODO: @haxxxton refactor to use form blocks
const VALIDATION_SCHEMA = yup.object({
  brokerDocumentId: yup
    .string()
    .trim()
    .matches(BROKER_DOCUMENT_ID_PATTERN, BROKER_DOCUMENT_ID_ERROR)
    .required('Broker Document ID is required'),
  sync: yup.boolean().default(false),
});

export const useBankStatementsBrokerForm = (
  modalApi: UseOpenCloseReturn,
  { uuid, definition, onSuccess }: UseBankStatementsBrokerFormOptions,
) => {
  const { runBankStatementsBrokerRequest, isPending } =
    useRunBankStatementsBrokerRequest();

  const resolver =
    useFormResolver<BankStatementsBrokerFormValues>(VALIDATION_SCHEMA);
  const form = useForm<BankStatementsBrokerFormValues>({
    defaultValues: DEFAULT_VALUES,
    resolver,
  });

  useEffect(() => {
    if (modalApi.open) {
      form.reset(DEFAULT_VALUES);
    }
  }, [modalApi.open]);

  const handleSubmit = useEventHandler(
    async (values: BankStatementsBrokerFormValues): Promise<void> => {
      try {
        await runBankStatementsBrokerRequest({
          data: {
            params: {
              brokerDocumentId: values.brokerDocumentId.trim(),
              sync: values.sync,
              uuid,
            },
          },
          documentParams: {
            definition,
          },
        });

        onSuccess?.();
        modalApi.onClose();
      } catch {
        // error handled in useRunBankStatementsBrokerRequest hook
      }
    },
  );

  // Contain submission to this modal. In the user journey the modal renders
  // inside the workflow step's own <form>; stopPropagation keeps an Enter-key
  // submit here from bubbling up and triggering that outer form.
  const onSubmit = useEventHandler(
    (event: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      form.handleSubmit(handleSubmit)(event);
    },
  );

  return { form, isLoading: isPending, onSubmit };
};
